import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';

interface HomeDashboardContextValue {
  setIsEditing: ((editing: boolean) => void) | null;
  register: (setter: (editing: boolean) => void) => void;
  unregister: () => void;
}

const HomeDashboardContext = createContext<HomeDashboardContextValue>({
  setIsEditing: null,
  register: () => {},
  unregister: () => {},
});

export function HomeDashboardProvider({ children }: { children: ReactNode }) {
  const [setter, setSetter] = useState<((editing: boolean) => void) | null>(null);
  const register = useCallback((fn: (editing: boolean) => void) => setSetter(() => fn), []);
  const unregister = useCallback(() => setSetter(null), []);
  return (
    <HomeDashboardContext.Provider value={{ setIsEditing: setter, register, unregister }}>
      {children}
    </HomeDashboardContext.Provider>
  );
}

export function useHomeDashboardContext() {
  return useContext(HomeDashboardContext);
}

export function useRegisterHomeDashboard(setIsEditing: (editing: boolean) => void) {
  const { register, unregister } = useHomeDashboardContext();
  useEffect(() => {
    register(setIsEditing);
    return unregister;
  }, [setIsEditing, register, unregister]);
}
